<template>
    <div class="page">
        <IndexHeader />

        <main class="page-main">
            <!-- 无 tenant_key 错误提示 -->
            <div v-if="!tenantKey" class="error-card">
                <div class="error-icon">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M12 9V13M12 17H12.01M5.07 19H18.93C20.47 19 21.44 17.33 20.66 16L13.73 4C12.96 2.67 11.04 2.67 10.27 4L3.34 16C2.56 17.33 3.53 19 5.07 19Z"
                            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                </div>
                <h2>访问错误</h2>
                <p>缺少租户标识（tenant_key），无法加载订单内容。</p>
                <p class="error-hint">请通过正确的租户小店链接访问。</p>
            </div>

            <!-- 订单内容 -->
            <div v-else class="orders-container">
                <!-- 返回小店按钮 -->
                <div class="back-btn" @click="goBack">
                    <LeftOutlined /> 返回小店
                </div>

                <!-- 订单查询卡片 -->
                <div class="orders-card">
                    <a-tabs v-model:activeKey="activeTab" class="orders-tabs">
                        <!-- 浏览器缓存订单 -->
                        <a-tab-pane key="cached" tab="最近订单">
                            <div v-if="cachedOrders.length > 0" class="cached-orders">
                                <div class="order-item" v-for="order in cachedOrders" :key="order.order_id"
                                    @click="viewOrderDetail(order)">
                                    <div class="order-item-left">
                                        <div class="order-goods">{{ order.goods_name || '未知商品' }}</div>
                                        <div class="order-id">{{ order.order_id }}</div>
                                    </div>
                                    <div class="order-item-right">
                                        <span class="order-amount" v-if="order.amount">￥{{ order.amount }}</span>
                                        <span class="order-status-tag" :class="'status-' + (order.status ?? 0)">
                                            {{ statusText(order.status) }}
                                        </span>
                                        <span class="order-date">{{ order.created_at }}</span>
                                    </div>
                                </div>
                            </div>
                            <a-empty v-else description="暂无最近订单" />
                        </a-tab-pane>

                        <!-- 密码查单 -->
                        <a-tab-pane key="search" tab="密码查单">
                            <div class="search-section">
                                <a-input-search v-model:value="orderQueryPassword" placeholder="输入订单查询密码"
                                    class="search-input" enter-button="查询" :loading="ordersLoading"
                                    @search="searchOrders" />
                                <!-- 最近使用的密码 -->
                                <div class="recent-passwords" v-if="recentPasswords.length > 0">
                                    <span class="recent-label">最近使用：</span>
                                    <a-tag v-for="pwd in recentPasswords" :key="pwd" class="pwd-tag"
                                        @click="orderQueryPassword = pwd; searchOrders()">
                                        {{ pwd }}
                                    </a-tag>
                                </div>
                            </div>

                            <a-spin :spinning="ordersLoading">
                                <div v-if="orderList.length > 0" class="order-list">
                                    <div class="order-card-item" v-for="order in orderList" :key="order.order_id">
                                        <div class="order-card-header">
                                            <span class="order-num">{{ order.order_id }}</span>
                                            <span class="order-status" :class="'status-' + order.status">
                                                {{ order.status_text }}
                                            </span>
                                        </div>
                                        <div class="order-card-body">
                                            <div class="order-goods-name">{{ order.goods_name }}</div>
                                            <div class="order-card-meta">
                                                <span>数量：{{ order.quantity }}</span>
                                                <span>金额：￥{{ order.amount }}</span>
                                                <span>邮箱：{{ order.email }}</span>
                                                <span>{{ order.created_at }}</span>
                                            </div>
                                        </div>
                                        <div class="order-card-actions">
                                            <a-button size="small" v-if="order.status === 0" type="primary"
                                                @click="goToPay(order)">去支付</a-button>
                                            <a-button size="small"
                                                @click="viewOrderDetailById(order.order_id)">查看详情</a-button>
                                        </div>
                                    </div>
                                    <div class="pagination-wrapper" v-if="orderTotal > orderPageSize">
                                        <a-pagination :current="orderPage" :page-size="orderPageSize"
                                            :total="orderTotal" :show-total="(total) => `共 ${total} 条`"
                                            @change="handleOrderPageChange" />
                                    </div>
                                </div>
                                <a-empty v-else-if="!ordersLoading && hasSearched" description="暂无订单记录" />
                            </a-spin>
                        </a-tab-pane>
                    </a-tabs>
                </div>
            </div>
        </main>

        <!-- 订单详情弹窗 -->
        <a-modal v-model:open="orderDetailVisible" title="订单详情" width="500px">
            <div class="order-detail-dialog" v-if="orderDetail">
                <div class="detail-row">
                    <span class="label">订单编号</span>
                    <span>{{ orderDetail.order_id }}</span>
                </div>
                <div class="detail-row">
                    <span class="label">商品名称</span>
                    <span>{{ orderDetail.goods_name }}</span>
                </div>
                <div class="detail-row">
                    <span class="label">订单金额</span>
                    <span class="amount">￥{{ orderDetail.amount }}</span>
                </div>
                <div class="detail-row">
                    <span class="label">购买数量</span>
                    <span>{{ orderDetail.quantity }}</span>
                </div>
                <div class="detail-row">
                    <span class="label">订单状态</span>
                    <span :class="'status-' + orderDetail.status">{{ orderDetail.status_text }}</span>
                </div>
                <div class="detail-row">
                    <span class="label">邮箱</span>
                    <span>{{ orderDetail.email }}</span>
                </div>
                <div class="detail-row">
                    <span class="label">支付方式</span>
                    <span>{{ orderDetail.pay_mode === 'tenant' ? '余额支付' : '在线支付' }}</span>
                </div>
                <div class="detail-row" v-if="orderDetail.auth_result">
                    <span class="label">授权结果</span>
                    <span class="auth-result">{{ orderDetail.auth_result }}</span>
                </div>
                <div class="detail-row">
                    <span class="label">下单时间</span>
                    <span>{{ orderDetail.created_at }}</span>
                </div>
                <div class="detail-row" v-if="orderDetail.paid_at">
                    <span class="label">支付时间</span>
                    <span>{{ orderDetail.paid_at }}</span>
                </div>
            </div>
            <template #footer>
                <a-button v-if="orderDetail && orderDetail.status === 0" type="primary"
                    @click="goToPay(orderDetail)">去支付</a-button>
                <a-button @click="orderDetailVisible = false">关闭</a-button>
            </template>
        </a-modal>

        <IndexFooter />
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import axios from 'axios';
import { message } from 'ant-design-vue';
import { LeftOutlined } from '@ant-design/icons-vue';
import IndexHeader from '@/components/IndexHeader.vue'
import IndexFooter from '@/components/IndexFooter.vue'

const baseURL = process.env.VUE_APP_API_BASE_URL;

export default defineComponent({
    name: 'ShopOrders',
    components: { IndexHeader, IndexFooter, LeftOutlined },
    data() {
        return {
            tenantKey: '',
            activeTab: 'cached',
            orderQueryPassword: '',
            orderList: [],
            ordersLoading: false,
            hasSearched: false,
            orderPage: 1,
            orderPageSize: 20,
            orderTotal: 0,
            cachedOrders: [],
            recentPasswords: [],
            orderDetailVisible: false,
            orderDetail: null,
        }
    },
    mounted() {
        this.tenantKey = this.$route.query.tenant_key || '';
        if (this.tenantKey) {
            this.loadCachedOrders();
            this.loadRecentPasswords();
        }
    },
    methods: {
        statusText(status) {
            const map = { 0: '待支付', 1: '已完成', 2: '已取消' };
            return map[status] ?? '未知';
        },
        goBack() {
            this.$router.push({ path: '/shop', query: { tenant_key: this.tenantKey } });
        },
        // 订单缓存
        loadCachedOrders() {
            const raw = localStorage.getItem(`shop_orders_${this.tenantKey}`);
            this.cachedOrders = raw ? JSON.parse(raw) : [];
        },
        cacheOrder(order) {
            this.cachedOrders.unshift(order);
            if (this.cachedOrders.length > 20) this.cachedOrders.pop();
            localStorage.setItem(`shop_orders_${this.tenantKey}`, JSON.stringify(this.cachedOrders));
        },
        // 最近密码缓存
        loadRecentPasswords() {
            const raw = localStorage.getItem(`shop_pwd_${this.tenantKey}`);
            this.recentPasswords = raw ? JSON.parse(raw) : [];
        },
        saveRecentPassword(pwd) {
            if (!pwd) return;
            // 去重并放到最前
            this.recentPasswords = [pwd, ...this.recentPasswords.filter(p => p !== pwd)].slice(0, 5);
            localStorage.setItem(`shop_pwd_${this.tenantKey}`, JSON.stringify(this.recentPasswords));
        },
        // 订单列表
        async searchOrders() {
            if (!this.orderQueryPassword) {
                message.warning('请输入订单查询密码');
                return;
            }
            this.saveRecentPassword(this.orderQueryPassword);
            this.orderPage = 1;
            this.getOrderList();
        },
        async getOrderList() {
            this.ordersLoading = true;
            this.hasSearched = true;
            try {
                const res = await axios.post(`${baseURL}/api/shop/order/list`, {
                    tenant_key: this.tenantKey,
                    query_password: this.orderQueryPassword,
                    page: this.orderPage,
                    pageSize: this.orderPageSize,
                });
                if (res.data.code === 0 || res.data.code === 200) {
                    this.orderList = res.data.data.list || [];
                    this.orderTotal = res.data.data.total || 0;
                } else {
                    message.error(res.data.msg || '查询失败');
                }
            } catch (e) {
                console.error('查询订单失败:', e);
                message.error('查询订单失败');
            } finally {
                this.ordersLoading = false;
            }
        },
        handleOrderPageChange(page) {
            this.orderPage = page;
            this.getOrderList();
        },
        // 去支付
        goToPay(order) {
            if (!order || order.status !== 0) return;
            // 查询订单详情获取支付链接
            this.getPayUrl(order);
        },
        async getPayUrl(order) {
            try {
                const res = await axios.post(`${baseURL}/api/shop/order/detail`, {
                    tenant_key: this.tenantKey,
                    order_id: order.order_id,
                    query_password: this.orderQueryPassword,
                });
                if (res.data.code === 0 || res.data.code === 200) {
                    const detail = res.data.data;
                    // 当面付：跳转二维码页面（pay_url是支付宝二维码URL，由前端生成二维码）
                    if (detail.pay_type === 'alipay_dmf') {
                        this.$router.push({
                            path: '/shopAlipayDMF',
                            query: {
                                tenant_key: this.tenantKey,
                                order_id: order.order_id,
                                query_password: this.orderQueryPassword,
                            }
                        });
                    } else if (detail.pay_url) {
                        // 易支付/网站支付：直接跳转pay_url
                        window.location.href = detail.pay_url;
                    } else {
                        message.warning('该订单暂无支付链接，请联系商家');
                    }
                } else {
                    message.error(res.data.msg || '获取支付信息失败');
                }
            } catch (e) {
                console.error('获取支付信息失败:', e);
                message.error('获取支付信息失败');
            }
        },
        // 订单详情
        async viewOrderDetail(order) {
            this.orderQueryPassword = order.query_password;
            await this.viewOrderDetailById(order.order_id);
        },
        async viewOrderDetailById(orderId) {
            try {
                const res = await axios.post(`${baseURL}/api/shop/order/detail`, {
                    tenant_key: this.tenantKey,
                    order_id: orderId,
                    query_password: this.orderQueryPassword,
                });
                if (res.data.code === 0 || res.data.code === 200) {
                    this.orderDetail = res.data.data;
                    this.orderDetailVisible = true;
                } else {
                    message.error(res.data.msg || '获取订单详情失败');
                }
            } catch (e) {
                console.error('获取订单详情失败:', e);
                message.error('获取订单详情失败');
            }
        },
    }
})
</script>

<style scoped lang="scss">
.page {
    min-height: 100vh;
    background: linear-gradient(180deg, #f8fafc 0%, #eef2ff 60%, #f8fafc 100%);
    display: flex;
    flex-direction: column;
}

.page-main {
    max-width: 1100px;
    margin: 0 auto;
    padding: 12vh 1.5rem 4rem;
    display: flex;
    flex-direction: column;
    height: 90vh;
    overflow-y: auto;
    width: 100%;
    box-sizing: border-box;
    scrollbar-width: none;
    -ms-overflow-style: none;
}

.page-main::-webkit-scrollbar {
    width: 0;
    height: 0;
    display: none;
}

/* 错误提示 */
.error-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 4rem 2rem;
    background: #fff;
    border-radius: 1.4rem;
    border: 1px solid rgba(226, 232, 240, 0.9);
    box-shadow: 0 22px 45px -30px rgba(239, 68, 68, 0.25);

    .error-icon {
        width: 64px;
        height: 64px;
        color: #ef4444;
        margin-bottom: 1rem;

        svg {
            width: 100%;
            height: 100%;
        }
    }

    h2 {
        font-size: 1.5rem;
        font-weight: 700;
        color: #0f172a;
        margin: 0 0 0.5rem;
    }

    p {
        font-size: 0.95rem;
        color: #64748b;
        margin: 0.2rem 0;
    }

    .error-hint {
        color: #94a3b8;
        font-size: 0.85rem;
        margin-top: 0.5rem;
    }
}

/* 返回按钮 */
.back-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    color: #2563eb;
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    margin-bottom: 1rem;

    &:hover {
        opacity: 0.8;
    }
}

/* 订单查询卡片 */
.orders-card {
    background: #fff;
    border-radius: 1.2rem;
    padding: 2rem;
    border: 1px solid rgba(226, 232, 240, 0.9);
    box-shadow: 0 12px 24px -16px rgba(15, 23, 42, 0.2);
}

.orders-tabs {
    :deep(.ant-tabs-nav) {
        margin-bottom: 1.5rem;
    }

    :deep(.ant-tabs-tab) {
        font-size: 1rem;
        font-weight: 500;
    }

    :deep(.ant-tabs-ink-bar) {
        background: linear-gradient(135deg, #2563eb, #3b82f6);
    }

    :deep(.ant-tabs-tab-active .ant-tabs-tab-btn) {
        color: #2563eb;
    }
}

/* 搜索区 */
.search-section {
    margin-bottom: 1.5rem;

    .search-input {
        max-width: 500px;
    }
}

.recent-passwords {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.4rem;
    margin-top: 0.6rem;
    max-width: 500px;

    .recent-label {
        font-size: 0.78rem;
        color: #94a3b8;
    }

    .pwd-tag {
        cursor: pointer;
        font-size: 0.75rem;

        &:hover {
            opacity: 0.8;
        }
    }
}

/* 最近订单 */
.cached-orders {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
}

.order-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.8rem 1rem;
    background: #f8fafc;
    border-radius: 0.7rem;
    cursor: pointer;
    transition: all 0.2s;
    border: 1px solid transparent;

    &:hover {
        background: #eff6ff;
        border-color: rgba(37, 99, 235, 0.2);
    }

    .order-item-left {
        display: flex;
        flex-direction: column;
        gap: 0.2rem;
        min-width: 0;
        flex: 1;
    }

    .order-goods {
        font-size: 0.88rem;
        font-weight: 600;
        color: #1e293b;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .order-id {
        font-size: 0.75rem;
        color: #94a3b8;
    }

    .order-item-right {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 0.2rem;
        flex-shrink: 0;
    }

    .order-amount {
        font-size: 0.88rem;
        font-weight: 700;
        color: #2563eb;
    }

    .order-status-tag {
        font-size: 0.72rem;
        padding: 0.1rem 0.5rem;
        border-radius: 9999px;
        font-weight: 600;

        &.status-0 {
            background: rgba(234, 179, 8, 0.12);
            color: #ca8a04;
        }

        &.status-1 {
            background: rgba(34, 197, 94, 0.12);
            color: #15803d;
        }

        &.status-2 {
            background: rgba(239, 68, 68, 0.12);
            color: #dc2626;
        }
    }

    .order-date {
        font-size: 0.72rem;
        color: #94a3b8;
    }
}

/* 订单列表 */
.order-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.order-card-item {
    padding: 1rem 1.2rem;
    border: 1px solid rgba(226, 232, 240, 0.9);
    border-radius: 0.8rem;
    background: #fff;
    transition: box-shadow 0.2s;

    &:hover {
        box-shadow: 0 8px 16px -10px rgba(15, 23, 42, 0.1);
    }
}

.order-card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
}

.order-num {
    font-size: 0.82rem;
    font-weight: 600;
    color: #475569;
}

.order-status {
    font-size: 0.75rem;
    padding: 0.15rem 0.6rem;
    border-radius: 9999px;
    font-weight: 600;

    &.status-0 {
        background: rgba(234, 179, 8, 0.12);
        color: #ca8a04;
    }

    &.status-1 {
        background: rgba(34, 197, 94, 0.12);
        color: #15803d;
    }

    &.status-2 {
        background: rgba(239, 68, 68, 0.12);
        color: #dc2626;
    }
}

.order-card-body {
    .order-goods-name {
        font-size: 0.95rem;
        font-weight: 600;
        color: #0f172a;
        margin-bottom: 0.3rem;
    }
}

.order-card-meta {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
    font-size: 0.8rem;
    color: #94a3b8;
}

.order-card-actions {
    margin-top: 0.6rem;
    display: flex;
    justify-content: flex-end;
}

/* 分页 */
.pagination-wrapper {
    display: flex;
    justify-content: center;
    margin: 2rem 0 1.5rem;

    :deep(.ant-pagination-item-active) {
        background: linear-gradient(135deg, #2563eb, #3b82f6);
        border-color: transparent;

        a {
            color: #fff;
        }
    }
}

/* 订单详情弹窗 */
.order-detail-dialog {
    .detail-row {
        display: flex;
        padding: 0.6rem 0;
        border-bottom: 1px solid #f1f5f9;

        .label {
            width: 90px;
            flex-shrink: 0;
            color: #94a3b8;
            font-size: 0.85rem;
        }

        .amount {
            color: #ef4444;
            font-weight: 700;
        }

        .auth-result {
            color: #15803d;
            font-weight: 500;
        }

        .status-0 {
            color: #ca8a04;
            font-weight: 600;
        }

        .status-1 {
            color: #15803d;
            font-weight: 600;
        }

        .status-2 {
            color: #dc2626;
            font-weight: 600;
        }
    }
}

/* 移动端响应式 */
@media (max-width: 768px) {
    .page-main {
        padding: 10vh 1rem 4rem;
    }

    .orders-card {
        padding: 1.2rem;
    }

    .order-card-meta {
        gap: 0.6rem;
        font-size: 0.75rem;
    }

    .search-section .search-input {
        max-width: 100%;
    }
}
</style>
